import type { ReactNode } from "react";
import { Card } from "./Card";
import { Button } from "./Button";
import { SkeletonCard } from "./Skeleton";
import { getErrorMessage } from "@/api/client";

interface QueryStateBoundaryProps {
  isLoading?: boolean;
  error?: unknown;
  onRetry?: () => void;
  errorTitle?: string;
  loadingFallback?: ReactNode;
  children: ReactNode;
}

/**
 * QueryStateBoundary — renders loading / error / content states for a query.
 *
 * Distinguishes "backend failed" from "no data": a failed fetch shows an
 * error card with a Retry button instead of falling through to an empty state.
 */
export function QueryStateBoundary({
  isLoading = false,
  error,
  onRetry,
  errorTitle = "Unable to load data",
  loadingFallback,
  children,
}: QueryStateBoundaryProps) {
  if (isLoading) {
    return <>{loadingFallback ?? <SkeletonCard />}</>;
  }

  if (error) {
    return (
      <Card className="border-danger/30 bg-danger/5">
        <div className="flex flex-col items-center text-center py-8 px-4">
          <span className="text-4xl mb-4" role="img" aria-label="Error">
            {"⚠️"}
          </span>
          <h3 className="text-lg font-semibold text-text-primary mb-2">
            {errorTitle}
          </h3>
          <p className="text-sm text-text-secondary max-w-md break-words">
            {getErrorMessage(error)}
          </p>
          {onRetry && (
            <div className="mt-4">
              <Button onClick={onRetry} size="sm">
                Retry
              </Button>
            </div>
          )}
        </div>
      </Card>
    );
  }

  return <>{children}</>;
}
